import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete, MatOption } from '@angular/material/autocomplete';
import { MatChipGrid, MatChipRow, MatChipRemove, MatChipInput } from '@angular/material/chips';
import { Tenant } from '@app/Models/tenant';
import { Observable, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { MatFormField, MatLabel, MatError } from '@angular/material/input';
import { AsyncPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'tenants-selection',
    templateUrl: './tenants-selection.component.html',
    styleUrls: ['./tenants-selection.component.less'],
    imports: [FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatChipGrid, MatChipRow, MatChipRemove, MatIcon, MatAutocompleteTrigger, MatChipInput, MatAutocomplete, MatOption, MatError, AsyncPipe]
})
export class TenantsSelectionComponent implements OnInit, OnDestroy {
  static readonly selectAllOptionId = '00000000-0000-0000-0000-000000000000';

  @Output() selectionChanged = new EventEmitter<Tenant[]>();
  @ViewChild('tenantInputCtrl') tenantInputCtrl: ElementRef<HTMLInputElement>;
  @ViewChild('tenantChipsCtrl') tenantChipsCtrl: MatChipGrid;
  @Input() allTenants$: Observable<Tenant[]>;

  readonly selectable = true;
  readonly removable = true;
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  tenantSelectionForm: UntypedFormGroup;
  filteredTenants$: Observable<Tenant[]>;
  selectedTenants: Tenant[] = [];

  private allTenantsSubscription: Subscription;
  private tenantListItems: Tenant[] = [];

  constructor(private fb: UntypedFormBuilder) { }

  ngOnInit(): void {
    this.buildForm();
    this.getTenantList();
  }

  public ngOnDestroy(): void {
    this.allTenantsSubscription?.unsubscribe();
  }

  remove(tenant: Tenant): void {
    const index = this.selectedTenants.map(e => e.TenantId).indexOf(tenant.TenantId);

    if (index >= 0) {
      this.selectedTenants.splice(index, 1);
      this.tenantSelectionForm.get('tenantInputCtrl').setValue(null);
      this.tenantSelectionForm.get('tenantChipsCtrl').setValue(this.selectedTenants);
      this.selectionChanged.emit(this.selectedTenants);
    }
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    if (event.option.value === TenantsSelectionComponent.selectAllOptionId) {
      this.selectedTenants = [this.createSelectAllTenantOption()];
    } else if (this.isAllTenantsSelected()) {
      this.selectedTenants = [this.tenantListItems.find(tenant => tenant.TenantId === event.option.value)];
    } else {
      this.selectedTenants.push(this.tenantListItems.find(tenant => tenant.TenantId === event.option.value));
    }
    this.tenantInputCtrl.nativeElement.value = '';
    this.tenantInputCtrl.nativeElement.blur();
    this.tenantSelectionForm.get('tenantInputCtrl').setValue('');
    this.tenantSelectionForm.get('tenantChipsCtrl').setValue(this.selectedTenants);
    this.selectionChanged.emit(this.selectedTenants);
  }

  private isAllTenantsSelected(): boolean {
    return this.selectedTenants.length > 0 && this.selectedTenants[0].TenantId === TenantsSelectionComponent.selectAllOptionId;
  }

  private filter(value: string): Tenant[] {

    if (!value) {
      return this.tenantListItems.filter(tenant => !this.selectedTenants?.includes(tenant));
    }

    return this.tenantListItems.filter((tenant) => {
      let searchTerm = `${tenant.Text} ${tenant.Value} ${tenant.CrmId} ${tenant.CrmName}`;

      return (new RegExp(value, 'i').test(searchTerm) && !this.selectedTenants?.includes(tenant));
    });
  }

  private getTenantList(): void {
    this.allTenantsSubscription = this.allTenants$.subscribe(
      tenants => {
        this.tenantListItems = [this.createSelectAllTenantOption()].concat(tenants);
        this.tenantSelectionForm.get('tenantInputCtrl').setValue(null);
        this.selectionChanged.emit(this.selectedTenants);
      }
    );
  }

  private validateTenants(tenants: UntypedFormControl): { validateTenantsArray: { valid: boolean } } {
    if (tenants.value && tenants.value.length === 0) {
      return {
        validateTenantsArray: { valid: false }
      };
    }

    return null;
  }

  private buildForm(): void {
    this.tenantSelectionForm = this.fb.group({
      tenantInputCtrl: [null],
      tenantChipsCtrl: [this.selectedTenants, this.validateTenants]
    });

    this.tenantSelectionForm
      .get('tenantChipsCtrl')
      .statusChanges.subscribe(
        status => (this.tenantChipsCtrl.errorState = status === 'INVALID')
      );

    this.filteredTenants$ = this.tenantSelectionForm.get('tenantInputCtrl').valueChanges.pipe(
      startWith(''),
      map((tenantName: string | null) => this.filter(tenantName))
    );
  }

  private createSelectAllTenantOption(): Tenant {
    const tenant = new Tenant();
    tenant.Text = 'All Tenants';
    tenant.TenantId = TenantsSelectionComponent.selectAllOptionId;
    return tenant;
  }
}
